import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { adminStatsService } from '../../../services/adminStatsService';
import { useSeasonContext } from '../../../context/SeasonContext';

const TeamComparisonItemBlock = ({ block, onUpdate, onDelete }) => {
  const [team1Name, setTeam1Name] = useState(block.team1_name || '');
  const [team2Name, setTeam2Name] = useState(block.team2_name || '');
  const [week, setWeek] = useState(block.week || '');
  const [team1Stats, setTeam1Stats] = useState(block.team1_stats || {});
  const [team2Stats, setTeam2Stats] = useState(block.team2_stats || {});
  const [teams, setTeams] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('PASSING');
  const { selectedSeason } = useSeasonContext();

  const statCategories = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'BLOCKING', 'KICKING'];

  useEffect(() => {
    if (selectedSeason?.id) {
      fetchTeams();
      fetchWeeks();
    }
  }, [selectedSeason?.id]);

  useEffect(() => {
    if (team1Name && week && selectedSeason?.id) {
      fetchTeamStats(team1Name, week, setTeam1Stats);
    }
  }, [team1Name, week, selectedSeason?.id]);

  useEffect(() => {
    if (team2Name && week && selectedSeason?.id) {
      fetchTeamStats(team2Name, week, setTeam2Stats);
    }
  }, [team2Name, week, selectedSeason?.id]);

  const fetchTeams = async () => {
    try {
      const playerList = await adminStatsService.getPlayersForSeason(selectedSeason.id);
      const uniqueTeams = [...new Set(playerList.map(p => p.team).filter(Boolean))].sort();
      setTeams(uniqueTeams);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchWeeks = async () => {
    try {
      const availableWeeks = await adminStatsService.getAllWeeksWithData();
      setWeeks(availableWeeks || []);
    } catch (error) {
      console.error('Error fetching weeks:', error);
    }
  };

  const fetchTeamStats = async (teamName, selectedWeek, setStatsFunc) => {
    if (!teamName || !selectedWeek || !selectedSeason?.id) return {};

    try {
      const stats = {};
      for (const category of statCategories) {
        const result = await adminStatsService.getStatsForWeekAndSeason(
          category,
          selectedWeek,
          selectedSeason.id
        );

        let playerData = [];
        if (result?.data && Array.isArray(result.data)) {
          playerData = result.data;
        } else if (Array.isArray(result)) {
          playerData = result;
        }

        const teamPlayers = playerData.filter(p => 
          (p.TEAM && p.TEAM.toLowerCase() === teamName.toLowerCase()) ||
          (p.team && p.team.toLowerCase() === teamName.toLowerCase())
        );

        if (teamPlayers.length > 0) {
          stats[category] = aggregateTeamStats(teamPlayers);
        }
      }
      setStatsFunc(stats);
      return stats;
    } catch (error) {
      console.error(`Error fetching stats for ${teamName}:`, error);
      return {};
    }
  };

  const aggregateTeamStats = (players) => {
    const aggregated = {
      TEAM: players[0]?.TEAM || players[0]?.team,
      PLAYER_COUNT: players.length
    };

    if (players.length === 0) return aggregated;

    const firstPlayer = players[0];
    const excludedKeys = new Set(['NAME', 'name', 'TEAM', 'team', 'ID', 'id', 'POS', 'pos', 'created_at', 'week', 'season_id']);
    
    const numericKeys = Object.keys(firstPlayer).filter(k => {
      if (excludedKeys.has(k)) return false;
      const val = firstPlayer[k];
      return val !== null && val !== undefined && !isNaN(val);
    });

    numericKeys.forEach(key => {
      const values = players.map(player => {
        const val = player[key];
        return typeof val === 'number' ? val : parseFloat(val) || 0;
      }).filter(v => !isNaN(v));
      
      if (values.length > 0) {
        aggregated[key] = values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    return aggregated;
  };

  const handleTeam1Change = async (e) => {
    const selectedTeam = e.target.value;
    setTeam1Name(selectedTeam);
    if (week) {
      const stats = await fetchTeamStats(selectedTeam, week, setTeam1Stats);
      onUpdate(block.id, {
        team1_name: selectedTeam,
        team1_stats: stats
      });
    } else {
      onUpdate(block.id, {
        team1_name: selectedTeam
      });
    }
  };

  const handleTeam2Change = async (e) => {
    const selectedTeam = e.target.value;
    setTeam2Name(selectedTeam);
    if (week) {
      const stats = await fetchTeamStats(selectedTeam, week, setTeam2Stats);
      onUpdate(block.id, {
        team2_name: selectedTeam,
        team2_stats: stats
      });
    } else {
      onUpdate(block.id, {
        team2_name: selectedTeam
      });
    }
  };

  const handleWeekChange = async (e) => {
    const selectedWeek = e.target.value;
    setWeek(selectedWeek);
    onUpdate(block.id, {
      week: selectedWeek
    });
    
    if (team1Name && selectedWeek) {
      const stats1 = await fetchTeamStats(team1Name, selectedWeek, setTeam1Stats);
      onUpdate(block.id, {
        team1_stats: stats1
      });
    }
    if (team2Name && selectedWeek) {
      const stats2 = await fetchTeamStats(team2Name, selectedWeek, setTeam2Stats);
      onUpdate(block.id, {
        team2_stats: stats2
      });
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value.toFixed(2);
    return value;
  };

  const compareValues = (v1, v2) => {
    if (!v1 || !v2) return 0;
    const n1 = parseFloat(v1);
    const n2 = parseFloat(v2);
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
    return 0;
  };

  const getStatKeys = () => {
    const t1Data = team1Stats[activeCategory];
    const t2Data = team2Stats[activeCategory];
    const excludedKeys = new Set(['TEAM', 'team', 'PLAYER_COUNT', 'NAME', 'name', 'POS', 'pos']);
    
    const keys = new Set();
    if (t1Data) {
      Object.keys(t1Data).forEach(k => {
        if (!excludedKeys.has(k)) keys.add(k);
      });
    }
    if (t2Data) {
      Object.keys(t2Data).forEach(k => {
        if (!excludedKeys.has(k)) keys.add(k);
      });
    }
    return Array.from(keys).sort();
  };

  const t1Data = team1Stats[activeCategory];
  const t2Data = team2Stats[activeCategory];
  const statKeys = getStatKeys();

  return (
    <div className="block-item">
      <div className="block-item-header">
        <h3>Team Comparison</h3>
        <button onClick={() => onDelete(block.id)} className="delete-btn">
          <Trash2 size={18} />
        </button>
      </div>

      <div className="field-group">
        <label>Week</label>
        <select
          value={week}
          onChange={handleWeekChange}
          disabled={loading || weeks.length === 0}
          className="field-select"
        >
          <option value="">Select week</option>
          {weeks.map((w) => (
            <option key={w} value={w}>
              Week {w}
            </option>
          ))}
        </select>
      </div>

      <div className="field-group">
        <label>Team 1</label>
        <select
          value={team1Name}
          onChange={handleTeam1Change}
          disabled={loading || teams.length === 0}
          className="field-select"
        >
          <option value="">Select team</option>
          {teams.map((team) => (
            <option key={team} value={team}>
              {team}
            </option>
          ))}
        </select>
      </div>

      <div className="field-group">
        <label>Team 2</label>
        <select
          value={team2Name}
          onChange={handleTeam2Change}
          disabled={loading || teams.length === 0}
          className="field-select"
        >
          <option value="">Select team</option>
          {teams.map((team) => (
            <option key={team} value={team}>
              {team}
            </option>
          ))}
        </select>
      </div>

      {team1Name && team2Name && week && (
        <>
          <div className="stat-categories">
            {statCategories.map((category) => (
              <button
                key={category}
                className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="comparison-table">
            <table>
              <thead>
                <tr>
                  <th>{team1Name}</th>
                  <th>Stat</th>
                  <th>{team2Name}</th>
                </tr>
              </thead>
              <tbody>
                {statKeys.map((key) => {
                  const v1 = t1Data?.[key];
                  const v2 = t2Data?.[key];
                  const comparison = compareValues(v1, v2);
                  return (
                    <tr key={key}>
                      <td className={comparison > 0 ? 'winning' : comparison < 0 ? 'losing' : ''}>
                        {formatValue(v1)}
                      </td>
                      <td className="stat-label">{key}</td>
                      <td className={comparison < 0 ? 'winning' : comparison > 0 ? 'losing' : ''}>
                        {formatValue(v2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {t1Data && (
            <p className="player-count">
              Team 1: {t1Data.PLAYER_COUNT} players in week {week}
            </p>
          )}
          {t2Data && (
            <p className="player-count">
              Team 2: {t2Data.PLAYER_COUNT} players in week {week}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default TeamComparisonItemBlock;
